import { type InfiniteData, infiniteQueryOptions, type QueryKey, useInfiniteQuery } from "@tanstack/react-query";

/** One page of a list endpoint: `{ data, next_cursor }`, with no total (CONVENTIONS.md). */
export interface CursorPage<T> {
  data: T[];
  next_cursor: string | null;
}

export interface CursorListOptions<T> {
  /** Include every filter, so each filter set is its own cached list. */
  queryKey: QueryKey;
  /** Fetches the page after `cursor` (undefined for the first). */
  fetchPage: (cursor: string | undefined) => Promise<CursorPage<T>>;
  enabled?: boolean;
}

/** The query behind a cursor-paginated list: each "Load more" fetches the next page and appends it. */
export const cursorListOptions = <T>({ queryKey, fetchPage, enabled }: CursorListOptions<T>) =>
  infiniteQueryOptions({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: CursorPage<T>) => last.next_cursor ?? undefined,
    enabled,
  });

/** Every row loaded so far, in order. */
export const rowsOf = <T>(pages: CursorPage<T>[] | undefined): T[] => pages?.flatMap((p) => p.data) ?? [];

/** A cursor-paginated list for a page: its rows so far, and whether there are more to load. */
export function useCursorList<T>(options: CursorListOptions<T>) {
  const query = useInfiniteQuery(cursorListOptions(options));
  return {
    rows: rowsOf(query.data?.pages),
    status: query.status,
    /** paused: offline, waiting for the network */
    fetchStatus: query.fetchStatus,
    /** whether a first page has come, even if a later refetch failed */
    loaded: query.data !== undefined,
    error: query.error,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
    refetch: () => void query.refetch(),
  };
}

/** Every row of a list, page after page: for short lists a page shows whole (a mapping table, a picker). */
export async function allPages<T>(fetchPage: (cursor: string | undefined) => Promise<CursorPage<T>>): Promise<T[]> {
  const rows: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await fetchPage(cursor);
    rows.push(...page.data);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  return rows;
}

/**
 * A loaded list with its rows changed in place, for `setQueriesData` after an action: a row the
 * API changed, or one it removed, shows at once without reloading every page.
 */
export function mapRows<T>(
  data: InfiniteData<CursorPage<T>> | undefined,
  change: (rows: T[]) => T[],
): InfiniteData<CursorPage<T>> | undefined {
  return data && { ...data, pages: data.pages.map((p) => ({ ...p, data: change(p.data) })) };
}
